import {
  ContractAccountingStatusModel,
  ContractModel,
  ContractsRepository,
  DocumentModel,
  ProductConditionsModel,
  ProductConditionsServiceModel,
  ProductConditionsSubProductsModel,
} from '@/Contract/domain'
import { ContractForm } from '@/Contract/ui/hooks'
import { HttpMethod, HttpModel } from '@/Shared/domain'
import { axiosFetch, createQueryString } from '@/Shared/infrastructure'
import {
  getAccountingStatus,
  getContractData,
  getProductConditions,
  getProductContract,
  processUnpaidInvoices,
} from '@/Shared/infrastructure/endpoints'

export function createApiContractRepository(): ContractsRepository {
  return new ApiContractRepository()
}

type ApiResponseBillingModel = {
  merchantTargetId: number
  addressRoadTypeName: string | null
  addressStreetName: string
  addressStreetNumber: string
  addressAdditionalData1: string
  addressAdditionalData12: string | null
  addressZipCode: string
  addressTown: string
}

type ApiBillingModel = {
  merchantId: number
  merchantParentId: number | null
  merchantParentName: string | null
  hasMerchantHardDependency: boolean
  merchantMasterId: number | null
  merchantMasterAlias: string | null
  merchantSuperMasterAlias: string | null
  merchantName: string
  merchantBusinessName: string 
  merchantCIF: string
  addressRoadTypeName: string | null
  addressStreetName: string
  addressStreetNumber: string
  addressAdditionalData1: string | null
  addressAdditionalData12: string | null
  addressZipCode: string
  addressTown: string
  addressProvince: string
  addressTelephone: string | null
  adressId: number
}

const convertBillingDataToDomain = (data: ApiBillingModel): ContractModel => ({
  name: data.merchantBusinessName ,
  afiliateCode: data.merchantId,
  cif: data.merchantCIF,

  street: data.addressStreetName,
  number: data.addressStreetNumber,
  additionalInfo: data.addressAdditionalData12,
  floorBlockStair: data.addressAdditionalData1,
  postalCode: data.addressZipCode,
  city: data.addressTown,
  province: data.addressProvince,

  addressId: data.adressId,
})

const convertFormToApi = (data: ContractForm): ApiResponseBillingModel => ({
  merchantTargetId: data.afiliateCode,
  addressRoadTypeName: null,
  addressStreetName: data.street,
  addressStreetNumber: data.number,
  addressAdditionalData1: data.floorBlockStair ?? '',
  addressAdditionalData12: data.additionalInformation ?? '',
  addressZipCode: data.postalCode,
  addressTown: data.city,
})

type ApiProductServicesModel = {
  id: number
  name: string
  value: string
  family: string
}

type ApiSubProductModel = {
  id: number
  name: string
  isOptional: boolean
  isNew: boolean
  services: ApiProductServicesModel[]
}

type ApiProductConditionsModel = {
  merchantId: number
  merchantParentId: null
  merchantParentName: null
  hasMerchantHardDependency: false
  merchantMasterId: null
  merchantMasterAlias: null
  merchantSuperMasterAlias: null
  merchantName: string
  merchantCIF: string
  contractOpenDate: string
  contractClosedDate: null
  paymentModeId: number
  paymentModeName: string
  paymentTermId: number
  paymentTermName: string
  bancAccountIBAN: null
  isElectronicInvoice: true
  services: ApiProductServicesModel[]
  subProducts: ApiSubProductModel[]
  isContractDownloadable: boolean
  amendmentStartDate: string
}

const convertProductConditionsToDomain = (
  data: ApiProductConditionsModel,
  productId: number,
): ProductConditionsModel => ({
  id: productId,
  contractOpenDate: data.contractOpenDate,
  paymentModeId: data.paymentModeId,
  isElectronicInvoice: data.isElectronicInvoice,
  paymentTermName: data.paymentTermName,
  isContractDownloadable: data.isContractDownloadable,
  amendmentStartDate: data.amendmentStartDate,
  services: data.services.map(
    (service: ApiProductServicesModel): ProductConditionsServiceModel => ({
      id: service.id,
      name: service.name,
      value: service.value,
      family: service.family,
    }),
  ),
  subProducts: data.subProducts.map(
    (subProduct: ApiSubProductModel): ProductConditionsSubProductsModel => ({
      id: subProduct.id,
      name: subProduct.name,
      isOptional: subProduct.isOptional,
      isNew: subProduct.isNew,
      services: subProduct.services.map(
        (service: ApiProductServicesModel): ProductConditionsServiceModel => ({
          id: service.id,
          name: service.name,
          value: service.value,
          family: service.family,
        }),
      ),
    }),
  ),
})

type ApiAccountingStatusModel = {
  merchantId: number
  merchantParentId: number | null
  merchantParentName: string | null
  hasMerchantHardDependency: boolean
  merchantMasterId: number | null
  merchantMasterAlias: string | null
  merchantSuperMasterAlias: string | null
  merchantName: string
  merchantCIF: string
  merchantDebtAmount: number
  merchantBlocked: boolean
}

const convertAccountingStatusToDomain = (
  data: ApiAccountingStatusModel,
): ContractAccountingStatusModel => ({
  debtAmount: data.merchantDebtAmount,
  isBlocked: data.merchantBlocked,
})

export class ApiContractRepository implements ContractsRepository {
  async GetBillingData(merchantTargetId: number): Promise<HttpModel<ContractModel>> {
    const response = await axiosFetch(
      getContractData.concat(
        createQueryString({
          merchantTargetId: merchantTargetId.toString(),
        }),
      ),
      HttpMethod.GET,
      {},
    )

    if (!response.data || !response.data.length) {
      return {
        data: {} as ContractModel,
        meta: response.meta,
      }
    }

    return {
      data: convertBillingDataToDomain(response.data[0]),
      meta: response.meta,
    }
  }

  async SaveBillingData(data: ContractForm): Promise<HttpModel<boolean>> {
    const response = await axiosFetch(
      getContractData,
      HttpMethod.POST,
      convertFormToApi(data),
    )

    return {
      data: response.data,
      meta: response.meta,
    }
  }

  async GetProductConditions(
    merchantTargetId: number,
    productId: number,
  ): Promise<HttpModel<ProductConditionsModel>> {
    const response = await axiosFetch(
      getProductConditions.concat(
        createQueryString({
          merchantTargetId: merchantTargetId.toString(),
          productId: productId.toString(),
        }),
      ),
      HttpMethod.GET,
      {},
    )

    if (!response.data) {
      return {
        data: {} as ProductConditionsModel,
        meta: response.meta,
      }
    }

    return {
      data: convertProductConditionsToDomain(response.data, productId),
      meta: response.meta,
    }
  }

  async GetProductContracts(
    merchantTargetId: number,
    productId: number,
  ): Promise<HttpModel<DocumentModel[]>> {
    const response = await axiosFetch(
      getProductContract.concat(
        createQueryString({
          merchantTargetId: merchantTargetId.toString(),
          product: productId.toString(),
        }),
      ),
      HttpMethod.GET,
      {},
    )

    if (!response.data) {
      return {
        data: [],
        meta: response.meta,
      }
    }

    return {
      data: response.data,
      meta: response.meta,
    }
  }

  async GetAccountingStatus(
    merchantTargetId: number,
  ): Promise<HttpModel<ContractAccountingStatusModel>> {
    const response = await axiosFetch(
      getAccountingStatus.concat(
        createQueryString({
          merchantTargetId: merchantTargetId.toString(),
        }),
      ),
      HttpMethod.GET,
      {},
    )

    if (!response.data) {
      return {
        data: {} as ContractAccountingStatusModel,
        meta: response.meta,
      }
    }

    return {
      data: convertAccountingStatusToDomain(response.data),
      meta: response.meta,
    }
  }

  async ProcessUnpaidInvoices(merchantTargetId: number): Promise<HttpModel<boolean>> {
    const response = await axiosFetch(processUnpaidInvoices, HttpMethod.POST, {
      merchantTargetId: merchantTargetId,
    })

    return {
      data: response.data,
      meta: response.meta,
    }
  }
}
